/**
 * Coalesces high-frequency inputs (streaming deltas) into one flush per animation frame, so a
 * burst of 200 `asst.delta`s costs one reducer pass and one React render — not 200.
 * Falls back to a timer when rAF is unavailable or the tab is hidden (rAF pauses there, and we
 * still want the view current when the user comes back).
 */
export class RafBatcher<T> {
  private queue: T[] = []
  private scheduled: number | ReturnType<typeof setTimeout> | null = null
  private usingRaf = false

  constructor(
    private readonly flush: (items: T[]) => void,
    private readonly env: {
      raf?: (cb: () => void) => number
      caf?: (id: number) => void
      hidden?: () => boolean
    } = {
      raf:
        typeof requestAnimationFrame === 'function' ? (cb) => requestAnimationFrame(cb) : undefined,
      caf:
        typeof cancelAnimationFrame === 'function' ? (id) => cancelAnimationFrame(id) : undefined,
      hidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
    },
  ) {}

  push(...items: T[]) {
    if (!items.length) return
    for (const i of items) this.queue.push(i)
    this.schedule()
  }

  /** Flush synchronously (history replay, tests, unmount). */
  drain() {
    this.cancel()
    if (!this.queue.length) return
    const items = this.queue
    this.queue = []
    this.flush(items)
  }

  get size() {
    return this.queue.length
  }

  cancel() {
    if (this.scheduled === null) return
    if (this.usingRaf) this.env.caf?.(this.scheduled as number)
    else clearTimeout(this.scheduled as ReturnType<typeof setTimeout>)
    this.scheduled = null
  }

  private schedule() {
    if (this.scheduled !== null) return
    const run = () => {
      this.scheduled = null
      this.drain()
    }
    if (this.env.raf && !this.env.hidden?.()) {
      this.usingRaf = true
      this.scheduled = this.env.raf(run)
    } else {
      this.usingRaf = false
      this.scheduled = setTimeout(run, 50)
    }
  }
}
